import styles from './CheckoutOrderDetails.module.scss';
import cn from 'classnames';
import { useCurrencyFormatter } from '@hooks/useCurrencyFormatter';
import { useMonthsYearsFormatter } from '@hooks/useMonthsYearsFormatter';
import { useContext } from 'react';
import { CartContext } from '@context/cart';
import { OfferItemType } from 'types/offer.types';

interface CheckoutOrderDetailsProps {
  title?: string;
  subtitle?: string;
  monthlyFinanceRate?: string;
  fundingPeriod?: string;
  interestFee?: string;
  totalAmountAfter?: string;
  numberOfProductsItems?: string;
  valueSource?: any;
}

export const CheckoutOrderDetails = ({
  title,
  subtitle,
  monthlyFinanceRate,
  fundingPeriod,
  interestFee,
  totalAmountAfter,
  numberOfProductsItems,
  valueSource,
}: CheckoutOrderDetailsProps) => {
  const { selectedFinancePricing } = useContext(CartContext);
  const { currencyFormatter } = useCurrencyFormatter();
  const { monthsYearsFormatter } = useMonthsYearsFormatter();

  if (!valueSource) {
    return null;
  }

  const mainOfferItems = valueSource?.items?.filter(item => item.offerItem.type === OfferItemType.Main);
  const preSelectedPricings = mainOfferItems?.map(item => item?.pricing);
  const differentDurations = preSelectedPricings?.some(pricing => pricing?.months !== preSelectedPricings?.[0]?.months);

  // Funding period is the same for all items in the cart
  const fundingPeriodValue =
    selectedFinancePricing?.months ||
    valueSource?.financeContract?.loanPeriod ||
    valueSource?.items?.filter(
      cartItem => (cartItem.offerItem && cartItem.offerItem?.type === OfferItemType.Main) || cartItem.plan
    )?.[0]?.pricing?.months;

  const formattedFundingPeriod = monthsYearsFormatter({ monthsValue: fundingPeriodValue });

  const mainCartItemsCount = valueSource?.items?.filter(item => item.offerItem.type === OfferItemType.Main)?.length;

  const totalMonthlyPrice =
    valueSource?.totalMonthlyPrice || valueSource.items?.reduce((total, item) => total + item.price, 0);

  return (
    <div>
      {title && <h2 className={styles.title}>{title}</h2>}
      <p className={styles.text}>
        {numberOfProductsItems}: {mainCartItemsCount}
      </p>

      <p className={styles.text}>{subtitle}:</p>

      <div className={cn(styles.offerDetails, styles.offerDetailsHeader)}>
        <p>{monthlyFinanceRate}</p>
        <p>
          {differentDurations
            ? '-'
            : currencyFormatter({
                price: totalMonthlyPrice,
              })}
        </p>
      </div>

      <div className={styles.offerDetails}>
        <p>{fundingPeriod}:</p>
        <p>{differentDurations ? '-' : formattedFundingPeriod}</p>
      </div>

      <div className={styles.offerDetails}>
        <p>{interestFee}:</p>
        <p>{differentDurations ? '-' : currencyFormatter({ price: 0 })}</p>
      </div>

      <div className={styles.offerDetails}>
        <p>{totalAmountAfter?.replace('{time}', differentDurations ? '-' : formattedFundingPeriod)}:</p>
        <p>{differentDurations ? '-' : currencyFormatter({ price: totalMonthlyPrice * fundingPeriodValue })}</p>
      </div>
    </div>
  );
};

export default CheckoutOrderDetails;
